import { Component } from 'react'
import styled from '@emotion/styled'
import { Box, Button, Modal, Icons } from 'stylewhere/components'
import { TrmEncodingVerifyTag, TrmEncodingVerifyGroupedTag } from 'stylewhere/api'
import { getGroupedKillTags } from 'stylewhere/utils'
import { T, __ } from 'stylewhere/i18n'

interface TagStep {
  label: string
  progress: boolean
  done: boolean
}

interface Props {
  tags: TrmEncodingVerifyTag[]
  visible: boolean
  replace: boolean
  onConfirm: (target: TrmEncodingVerifyTag[]) => void
  onClose: (resetReplacing?: boolean) => void
  progress?: boolean
  selected?: TrmEncodingVerifyGroupedTag
}

interface State {
  identifier?: TrmEncodingVerifyGroupedTag
  progress: boolean
}

export class IdentifierListModal extends Component<Props, State> {
  state: State = {
    identifier: this.props.selected,
    progress: this.props.progress ?? false
  }

  /* select identifier group */
  chooseIdentifier = (tag) => {
    const { identifier } = this.state
    const { replace } = this.props
    if (tag !== identifier && ((!replace && tag.tokill) || (replace && tag.associate))) {
      this.setState({ identifier: tag })
    }
  }

  _confirm = () => {
    this.setState({ progress: true }, this._doConfirm)
  }

  _doConfirm = () => {
    const { onConfirm } = this.props
    const { identifier } = this.state
    onConfirm(identifier.tags)
  }

  _close = () => {
    this.setState({ identifier: undefined, progress: false }, this.callback)
  }

  callback = () => {
    const { onClose } = this.props
    onClose()
  }

  _closeReplacing = () => {
    this.setState({ identifier: undefined, progress: false }, this.callbackReplacing)
  }

  callbackReplacing = () => {
    const { onClose } = this.props
    onClose(true)
  }

  /* get steps */
  getProgressSteps = () => {
    const { tags, replace } = this.props
    const { identifier } = this.state
    if (!identifier) return []
    const groupedTags = getGroupedKillTags(tags, replace)
    const selected = groupedTags.find((g) => g.code === identifier.code)
    const step: TagStep[] = []
    if (selected) {
      const disassociate = selected.tags.find((tg) => tg.itemId && tg.itemId !== '' && tg.associate)
      // add step if not replace and disassociating item
      if (!replace && disassociate) {
        step.push({
          label: __(T.misc.disassociating_item),
          progress: !disassociate.disassociate,
          done: disassociate.disassociate
        })
      }
      // add step for kill or replace
      selected.tags.map((tg) => {
        if ((!replace && tg.tokill) || (replace && tg.associate)) {
          step.push({
            label: replace ? __(T.misc.reading_tag) : __(T.misc.killing_tag, { type: tg.type, role: tg.role }),
            progress: tg.progress,
            done: tg.done
          })
        }

        if (replace && tg.towrite) {
          step.push({
            label: tg.replacingCode ? __(T.misc.writing_tag_with, { tag: tg.replacingCode }) : __(T.misc.writing_tag),
            progress: tg.writing,
            done: tg.writed
          })
        }

        // add step for replacing operation
        if (replace && tg.associate) {
          if (tg.towrite) {
            step.push({
              label: tg.desiderCode ? __(T.misc.replace_tag_with, { tag: tg.desiderCode }) : __(T.misc.replace_tag),
              progress: tg.replacing,
              done: tg.replaced
            })
          } else {
            step.push({
              label: tg.replacingCode ? __(T.misc.replace_tag_with, { tag: tg.replacingCode }) : __(T.misc.replace_tag),
              progress: tg.replacing,
              done: tg.replaced
            })
          }
        }
      })
    }
    return step
  }

  /* get modal title */
  getTitle = () => {
    const { replace, progress } = this.props
    if (!replace) return progress ? __(T.misc.kill_in_progress) : __(T.misc.confirm_kill_tag)
    return progress ? __(T.misc.replace_in_progress) : __(T.misc.confirm_replace_tag)
  }

  checkViewGroup = (group: TrmEncodingVerifyGroupedTag) => {
    let cont = 0
    group.tags.map((tag) => {
      if (this.checkViewTag(tag)) {
        cont++
      }
    })
    return cont > 0
  }

  checkViewTag = (tag: TrmEncodingVerifyTag) => {
    const { replace } = this.props
    return !tag.onlyforreplace || (replace && tag.onlyforreplace)
  }

  render() {
    const { visible, tags, replace } = this.props
    const { identifier, progress } = this.state
    const btn1 = { flex: 1, minWidth: 'auto', height: 60 }
    const groupedTags = getGroupedKillTags(tags, replace)
    const steps = progress ? this.getProgressSteps() : []
    const replacing = progress ? groupedTags.find((group) => group.code === identifier.code) : undefined
    return (
      <Modal visible={visible} onClose={progress ? undefined : this._close} size="3xl">
        <Box mb={30}>
          <Title>{this.getTitle()}</Title>
        </Box>
        {groupedTags.map((grouped, index) => {
          const isselected = identifier && identifier.code === grouped.code
          return this.checkViewGroup(grouped) && (!progress || isselected) ? (
            <IdentfierBox
              disabled={!replace ? !grouped.tokill : !grouped.associate}
              key={'group_' + index}
              mt={index > 0 ? 10 : 0}
              onClick={() => this.chooseIdentifier(grouped)}
            >
              {!progress && <Radio>{isselected && <RadioSelected />}</Radio>}
              <IdentfierInfo>
                {grouped.tags.map((tag, indexTag) => {
                  return this.checkViewTag(tag) ? (
                    <IdentifierRow key={'tag_' + indexTag}>
                      <IdentifierType read={!replace ? tag.tokill : tag.read}>{tag.type}</IdentifierType>
                      <Identifier>{tag.code}</Identifier>
                      {!!tag.role && <IdentifierRole>{tag.role}</IdentifierRole>}
                    </IdentifierRow>
                  ) : null
                })}
              </IdentfierInfo>
            </IdentfierBox>
          ) : null
        })}
        {progress && (
          <StepBox>
            {steps.map((step, index) => {
              return (
                <Step key={'step-' + index}>
                  {step.progress ? (
                    <Icons.Loader width={20} height={20} />
                  ) : step.done ? (
                    <Icons.CheckGreen width={20} height={20} />
                  ) : (
                    <StepWait />
                  )}
                  <StepLabel>{step.label}</StepLabel>
                </Step>
              )
            })}
          </StepBox>
        )}
        {!progress && (
          <Box row mt={30}>
            <Button
              variant="default"
              title={__(T.misc.cancel)}
              onClick={this._close}
              style={{ ...btn1, marginRight: 8 }}
            />
            <Button
              disabled={!identifier}
              title={__(T.misc.confirm)}
              onClick={this._confirm}
              style={{ ...btn1, marginLeft: 8 }}
            />
          </Box>
        )}
        {replace && progress && replacing && replacing.tags.length > 0 && !replacing.tags[0].replacing && (
          <Box row mt={30} center>
            <Button
              variant="default"
              title={__(T.misc.cancel)}
              onClick={this._closeReplacing}
              style={{ ...btn1, marginRight: 0, maxWidth: '50%' }}
            />
          </Box>
        )}
      </Modal>
    )
  }
}

const Title = styled.span`
  font-size: 26px;
  font-weight: 700;
  text-align: center;
  margin-top: 10px;
`

const IdentfierBox = styled(Box)`
  border: 2px solid #dedede;
  border-radius: 10px;
  padding-top: 20px;
  padding-bottom: 10px;
  padding-left: 15px;
  padding-right: 20px;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: flex-start;
`

const Radio = styled(Box)`
  width: 24px;
  height: 24px;
  border: 1px solid #dedede;
  border-radius: 15px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 3px;
  margin-right: 15px;
`

const RadioSelected = styled(Box)`
  width: 14px;
  height: 14px;
  border-radius: 10px;
  background-color: #3479ff;
`

const IdentfierInfo = styled(Box)`
  flex: 1;
  flex-direction: column;
`

const IdentifierRow = styled(Box)`
  flex: 1;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  margin-bottom: 5px;
`

const IdentifierType = styled.label<{ read?: boolean }>`
  margin-left: 0px;
  font-weight: 700;
  font-size: 16px;
  color: #333333;
  background-color: ${({ read }) => (read ? '#75EBA8' : '#f0f0f0')};
  border-radius: 5px;
  padding-left: 5px;
  padding-right: 5px;
  border: 1px solid ${({ read }) => (read ? '#75EBA8' : '#dedede')};
`

const IdentifierRole = styled.label`
  margin-left: 10px;
  font-weight: 700;
  font-size: 18px;
  color: #000000;
`

const Identifier = styled.label`
  flex: 1;
  margin-left: 10px;
  font-weight: 400;
  font-size: 20px;
  color: #000000;
`

const StepBox = styled(Box)`
  margin-top: 15px;
  margin-left: 30px;
  margin-right: 30px;
`

const Step = styled(Box)`
  flex: 1;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  margin-top: 10px;
`

const StepLabel = styled.label`
  flex: 1;
  margin-left: 10px;
  font-weight: 600;
  font-size: 18px;
  color: #333333;
`

const StepWait = styled(Box)`
  width: 20px;
  height: 20px;
  border: 1px solid #dedede;
  border-radius: 12px;
`
